import { Box, Button, Paper, Stack } from '@mui/material'
import { FC } from 'react'
import { NOT_AVAILABLE } from './cameraOverviewEnums'
import NotAvailableIcon from '../../../assets/images/not-available-icon.svg?react'
import _ from 'lodash'
import { LoggerName, namedLogger } from '../../../utils/logger'
import { ALL_APPLET_NAMES } from '../../../constants'

const logger = namedLogger(LoggerName.ConfirmationModal)

interface ConfirmationModalProps {
  locationName: string
  locationId: string
  cameraName: string
  action: string
  reason: string
  dates: string[]
  templateName: string
  templateText: string
  isLoading?: boolean
  onConfirm: (confirmed: boolean) => void
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  locationName,
  locationId,
  cameraName,
  action,
  reason,
  dates,
  templateName,
  templateText,
  isLoading = false,
  onConfirm
}) => {
  logger.info(`Confirmation Modal opened for locationId '${locationId}'`, {
    context: {
      applet: ALL_APPLET_NAMES.MONITORING_MODERATION,
      locationId: locationId ?? 'unknown'
    }
  })

  const templateTextIsAvailable = templateText !== NOT_AVAILABLE

  return (
    <Paper data-testid="confirmation-modal">
      <Stack direction="column" sx={{ height: 'fit-content', width: '56.25rem', gap: 'var(--spacing-s)', paddingBlockEnd: 'var(--spacing-s)' }}>
        <Stack
          direction="column"
          sx={{
            borderBottom: '0.0625rem solid var(--border-medium)',
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          <Stack direction="column" sx={{ padding: 'var(--spacing-m) var(--spacing-m) var(--spacing-s) var(--spacing-m)', gap: '0.25rem' }}>
            <Box sx={{ fontSize: 'var(--size-l)', fontWeight: '500' }}>Review Information</Box>
            <Box sx={{ fontSize: 'var(--size-xs)', fontWeight: '500', color: 'var(--text-secondary)' }}>
              We'll share this information with the customer. Carefully review it before sending.
            </Box>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'space-between', padding: 'var(--spacing-s) var(--spacing-m)' }}>
            <Stack direction="column" sx={{ width: '30%' }}>
              <Box sx={{ fontSize: '1rem', fontWeight: '500' }}>Documentation Summary</Box>
              <Stack direction="column" sx={{ gap: '0.5rem', paddingBlockStart: 'var(--spacing-s)' }}>
                <Stack direction="column" sx={{ gap: '0.5rem' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--text-secondary)' }}>Location & SID</Box>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <Box
                      sx={{
                        width: 'fit-content',
                        height: '2rem',
                        alignContent: 'center',
                        fontSize: 'var(--size-xs)',
                        fontWeight: '400',
                        padding: '0 var(--spacing-2xs)',
                        borderRadius: '0.375rem',
                        background: 'var(--bg-page)'
                      }}
                    >
                      {locationName}
                    </Box>
                    <Box
                      sx={{
                        width: 'fit-content',
                        height: '2rem',
                        alignContent: 'center',
                        fontSize: 'var(--size-xs)',
                        fontWeight: '400',
                        padding: '0 var(--spacing-2xs)',
                        borderRadius: '0.375rem',
                        background: 'var(--bg-page)'
                      }}
                    >
                      {locationId}
                    </Box>
                  </Stack>
                </Stack>
                <Stack direction="column" sx={{ gap: '0.5rem' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--text-secondary)' }}>Camera Name</Box>
                  <Box
                    sx={{
                      width: 'fit-content',
                      height: '2rem',
                      alignContent: 'center',
                      fontSize: 'var(--size-xs)',
                      fontWeight: '400',
                      padding: '0 var(--spacing-2xs)',
                      borderRadius: '0.375rem',
                      background: 'var(--bg-page)'
                    }}
                  >
                    {cameraName}
                  </Box>
                </Stack>
                <Stack direction="column" sx={{ gap: '0.5rem' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--text-secondary)' }}>Action</Box>
                  <Box
                    sx={{
                      width: 'fit-content',
                      height: '2rem',
                      alignContent: 'center',
                      fontSize: 'var(--size-xs)',
                      fontWeight: '400',
                      padding: '0 var(--spacing-2xs)',
                      borderRadius: '0.375rem',
                      background: 'var(--bg-page)'
                    }}
                  >
                    {action}
                  </Box>
                </Stack>
                <Stack direction="column" sx={{ gap: '0.5rem' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--text-secondary)' }}>Reason</Box>
                  <Box
                    sx={{
                      width: 'fit-content',
                      height: '2rem',
                      alignContent: 'center',
                      fontSize: 'var(--size-xs)',
                      fontWeight: '400',
                      padding: '0 var(--spacing-2xs)',
                      borderRadius: '0.375rem',
                      background: 'var(--bg-page)'
                    }}
                  >
                    {_.startCase(reason)}
                  </Box>
                </Stack>
                <Stack direction="column" sx={{ gap: '0.5rem' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--text-secondary)' }}>Incident Dates</Box>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    {dates.map((dateValue, idx) => (
                      <Box
                        key={dateValue + idx}
                        sx={{
                          width: 'fit-content',
                          height: '2rem',
                          alignContent: 'center',
                          fontSize: 'var(--size-xs)',
                          fontWeight: '400',
                          padding: '0 var(--spacing-2xs)',
                          borderRadius: '0.375rem',
                          background: 'var(--bg-page)'
                        }}
                      >
                        {dateValue}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
                <Stack direction="column" sx={{ gap: '0.5rem' }}>
                  <Box sx={{ fontSize: '0.875rem', fontWeight: '400', color: 'var(--text-secondary)' }}>Email Template</Box>
                  <Box
                    sx={{
                      width: 'fit-content',
                      height: '2rem',
                      alignContent: 'center',
                      fontSize: 'var(--size-xs)',
                      fontWeight: '400',
                      padding: '0 var(--spacing-2xs)',
                      borderRadius: '0.375rem',
                      background: 'var(--bg-page)'
                    }}
                  >
                    {templateName}
                  </Box>
                </Stack>
              </Stack>
            </Stack>
            {templateTextIsAvailable ? (
              <Box
                sx={{
                  width: '34rem',
                  minHeight: '34rem',
                  whiteSpace: 'pre-wrap',
                  background: 'var(--bg-page)',
                  borderRadius: '0.5rem',
                  fontSize: 'var(--size-2xs)',
                  fontWeight: '500',
                  gap: '0.625rem',
                  padding: 'var(--spacing-s)'
                }}
              >
                {templateText}
              </Box>
            ) : (
              <Stack
                direction="column"
                sx={{
                  width: '34rem',
                  minHeight: '34rem',
                  background: 'var(--bg-page)',
                  borderRadius: '0.5rem',
                  fontSize: 'var(--size-2xs)',
                  fontWeight: '500',
                  gap: '0.625rem',
                  padding: 'var(--spacing-s)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'var(--text-secondary)'
                }}
              >
                <NotAvailableIcon />
                <Box sx={{ color: 'var(--text-primary)' }}>No preview can be loaded</Box>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" sx={{ gap: '0.75rem', justifyContent: 'flex-end', padding: '0 1.5rem', position: 'sticky' }}>
          <Button
            data-testid="back-to-edit-button"
            variant="outlined"
            onClick={() => onConfirm(false)}
            sx={{ height: '3.5rem', fontSize: 'var(--size-s)' }}
          >
            Back to Edit
          </Button>
          <Button
            data-testid="send-communication-button"
            variant="contained"
            color="primary"
            onClick={() => onConfirm(true)}
            loading={isLoading}
            loadingPosition="end"
            sx={{ height: '3.5rem', fontSize: 'var(--size-s)' }}
          >
            {isLoading ? 'Sending...' : 'Send Communication'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
